import { Loader2, Wifi, WifiOff } from 'lucide-react';

function getProgressDisplay(progress, progressPercentage) {
  // Recursive crawl: image post-processing phase
  if (progress.image_page_total > 0) {
    const pageShare = progress.image_page_total > 0 ? (progress.image_page_idx / progress.image_page_total) : 0;
    const perPage = progress.images_current_total > 0 ? (progress.images_current_done / progress.images_current_total) : 0;
    const overall = Math.min(100, Math.round((pageShare + perPage / progress.image_page_total) * 100));
    return {
      label: `Images : page ${Math.min(progress.image_page_idx + 1, progress.image_page_total)}/${progress.image_page_total} · ${progress.images_current_done || 0}/${progress.images_current_total || 0}`,
      percentage: overall,
    };
  }

  // Simple / batch crawl: image analysis phase
  if (progress.images_total > 0) {
    const pct = Math.round((progress.images_done / progress.images_total) * 100);
    return { label: `Image ${progress.images_done} / ${progress.images_total}`, percentage: pct };
  }

  if (progress.url_total > 0) {
    const pct = Math.round((progress.url_index / progress.url_total) * 100);
    return { label: `URL ${progress.url_index} / ${progress.url_total}`, percentage: pct };
  }

  return { label: `Page ${progress.current_page || 0} / ${progress.max_pages || 1}`, percentage: progressPercentage };
}

function ProgressBar({ percentage }) {
  return (
    <div className="space-y-2">
      <div className="h-2 overflow-hidden rounded-full bg-zinc-800">
        <div className="h-full bg-blue-500 transition-all duration-300 ease-out" style={{ width: `${percentage}%` }} />
      </div>
    </div>
  );
}

function StatItem({ color, icon, label }) {
  return (
    <div className="flex items-center gap-1 text-zinc-400">
      <span className={color}>{icon}</span>
      <span>{label}</span>
    </div>
  );
}

function BatchStats({ progress }) {
  return (
    <div className="flex gap-4 text-xs">
      <StatItem color="text-green-400" icon={'\u2713'} label={`${progress.indexed || 0} indexées`} />
      <StatItem color="text-yellow-400" icon={'\u21BA'} label={`${progress.duplicates || 0} doublons`} />
      <StatItem color="text-red-400" icon={'\u2717'} label={`${progress.errors || 0} erreurs`} />
    </div>
  );
}

function RecursiveStats({ progress }) {
  return (
    <div className="flex gap-4 text-xs">
      <StatItem color="text-green-400" icon={'\u2713'} label={`${progress.pages_found || 0} indexées`} />
      <StatItem color="text-yellow-400" icon={'\u21BA'} label={`${progress.duplicates_found || 0} doublons`} />
      <StatItem color="text-zinc-500" icon={'\u2013'} label={`${progress.pages_skipped || 0} exclues`} />
      {progress.images_analyzed > 0 ? (
        <StatItem color="text-blue-400" icon={'\u25A3'} label={`${progress.images_analyzed} images`} />
      ) : null}
    </div>
  );
}

function ConnectionBadge({ polling }) {
  if (polling) {
    return (
      <span data-testid="crawl-connection-badge-polling" className="inline-flex items-center gap-1 rounded-full border border-amber-700/60 bg-amber-900/30 px-2 py-0.5 text-[10px] font-medium text-amber-400">
        <WifiOff className="h-3 w-3" />
        Suivi en arrière-plan
      </span>
    );
  }
  return (
    <span data-testid="crawl-connection-badge-live" className="inline-flex items-center gap-1 rounded-full border border-emerald-700/60 bg-emerald-900/20 px-2 py-0.5 text-[10px] font-medium text-emerald-400">
      <Wifi className="h-3 w-3" />
      Temps réel
    </span>
  );
}

export function CrawlProgressPanel({ crawling, progress, progressPercentage }) {
  if (!crawling || !progress) return null;

  const { label, percentage } = getProgressDisplay(progress, progressPercentage);

  return (
    <div data-testid="crawl-progress-panel" className="space-y-3 rounded-sm border border-blue-800 bg-blue-900/20 p-4">
      <div className="flex items-center gap-3">
        <Loader2 className="h-5 w-5 flex-shrink-0 animate-spin text-blue-400" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p data-testid="crawl-progress-status" className="flex-1 truncate text-sm font-medium text-blue-400">{progress.status}</p>
            <ConnectionBadge polling={Boolean(progress._polling)} />
          </div>
          {progress.current_url ? <p className="mt-1 truncate text-xs text-zinc-500" data-testid="crawl-progress-current-url">{progress.current_url}</p> : null}
        </div>
      </div>

      <ProgressBar percentage={percentage} />
      <div className="flex justify-between text-xs text-zinc-400">
        <span data-testid="crawl-progress-label">{label}</span>
        <span>{percentage}%</span>
      </div>

      {progress.url_total > 0 ? <BatchStats progress={progress} /> : <RecursiveStats progress={progress} />}
    </div>
  );
}
